import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  EmptyState,
} from '../ui';
import { api } from '../../lib/api';
import { useToast } from '../ui/toast';
import type { PasskeyInfo } from '@leal-crm/shared';
import { KeyRound } from 'lucide-react';

interface PasskeyListProps {
  passkeys: PasskeyInfo[];
  hasPassword: boolean;
  onRefresh: () => void;
}

export function PasskeyList({ passkeys, hasPassword, onRefresh }: PasskeyListProps) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPasskey, setSelectedPasskey] = useState<PasskeyInfo | null>(null);
  const [newName, setNewName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canRemove = passkeys.length > 1 || hasPassword;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openRenameModal = (passkey: PasskeyInfo) => {
    setSelectedPasskey(passkey);
    setNewName(passkey.deviceName || '');
    setRenameModalOpen(true);
  };

  const openDeleteModal = (passkey: PasskeyInfo) => {
    setSelectedPasskey(passkey);
    setDeleteModalOpen(true);
  };

  const handleRename = async () => {
    if (!selectedPasskey || !newName.trim()) return;

    setIsSubmitting(true);
    try {
      await api.patch(`/api/auth/passkeys/${selectedPasskey.id}`, {
        deviceName: newName.trim(),
      });
      addToast({ variant: 'success', title: t('passkey.renamed') });
      setRenameModalOpen(false);
      setSelectedPasskey(null);
      onRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      addToast({ variant: 'destructive', title: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPasskey) return;

    setIsSubmitting(true);
    try {
      await api.delete(`/api/auth/passkeys/${selectedPasskey.id}`);
      addToast({ variant: 'success', title: t('passkey.removed') });
      setDeleteModalOpen(false);
      setSelectedPasskey(null);
      onRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      addToast({ variant: 'destructive', title: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (passkeys.length === 0) {
    return (
      <EmptyState
        icon={KeyRound}
        title={t('passkey.noPasskeys')}
        description="No tienes passkeys registrados"
      />
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('passkey.deviceName')}</TableHead>
              <TableHead>{t('passkey.createdAt')}</TableHead>
              <TableHead>{t('passkey.lastUsed')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {passkeys.map((passkey) => (
              <TableRow key={passkey.id}>
                <TableCell className="font-medium">
                  {passkey.deviceName || 'Passkey sin nombre'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(passkey.createdAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(passkey.lastUsedAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openRenameModal(passkey)}
                    >
                      {t('passkey.rename')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteModal(passkey)}
                      disabled={!canRemove}
                    >
                      {t('passkey.remove')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {!canRemove && (
        <p className="mt-2 text-sm text-warning">
          {t('passkey.cannotRemoveLast')}
        </p>
      )}

      {/* Rename Dialog */}
      <Dialog open={renameModalOpen} onOpenChange={setRenameModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('passkey.rename')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="deviceName">{t('passkey.deviceName')}</Label>
              <Input
                id="deviceName"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Mi ordenador, iPhone, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRename} loading={isSubmitting}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('passkey.remove')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('passkey.confirmRemove')}</p>
            <p className="font-medium">
              {selectedPasskey?.deviceName || 'Passkey sin nombre'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} loading={isSubmitting}>
              {t('passkey.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
